import { useCallback, useEffect, useMemo, useReducer } from "react";

import {
  JOURNEY_MACHINE_EVENT,
} from "./journeyConstants";
import {
  activeJourneyStep,
} from "./journeyProjection";
import {
  createJourneyMachineState,
  journeyStateReducer,
} from "./journeyStateMachine";

export const useJourneyStateMachine = (
  steps,
  questionIndex,
  setQuestionIndex,
  preferProjectedStep = false,
) => {
  const projectedStep = useMemo(
    () => activeJourneyStep(steps, questionIndex),
    [questionIndex, steps],
  );
  const stepSignature = useMemo(
    () => steps.map(
      (step) => `${step.id}:${step.startIndex}:${step.endIndex}`,
    ).join("|"),
    [steps],
  );
  const [state, dispatch] = useReducer(
    journeyStateReducer,
    projectedStep?.id || null,
    createJourneyMachineState,
  );

  useEffect(() => {
    dispatch({
      type: JOURNEY_MACHINE_EVENT.rebase,
      steps,
      stepId: projectedStep?.id || null,
    });
  }, [projectedStep?.id, stepSignature]);

  const activeStep = useMemo(
    () => preferProjectedStep
      ? projectedStep
        || steps.find((step) => step.id === state.currentStepId)
        || null
      : steps.find((step) => step.id === state.currentStepId)
        || projectedStep
        || null,
    [preferProjectedStep, projectedStep, state.currentStepId, steps],
  );

  const goNext = useCallback(() => {
    const next = steps.find((step) => step.id === activeStep?.nextTargetId);
    if (!next) return false;
    setQuestionIndex(next.startIndex);
    dispatch({
      type: JOURNEY_MACHINE_EVENT.navigate,
      steps,
      stepId: next.id,
    });
    return true;
  }, [activeStep?.nextTargetId, setQuestionIndex, steps]);

  const goBack = useCallback(() => {
    if (!activeStep || activeStep.backTargetId === null) return false;
    const target = steps.find(
      (step) => step.id === activeStep.backTargetId,
    );
    if (!target) return false;
    setQuestionIndex(target.startIndex);
    dispatch({
      type: JOURNEY_MACHINE_EVENT.back,
      steps,
      stepId: target.id,
    });
    return true;
  }, [activeStep, setQuestionIndex, steps]);

  const goTo = useCallback((stepId, replaceHistory = false) => {
    const target = steps.find((step) => step.id === stepId);
    if (!target) return false;
    setQuestionIndex(target.startIndex);
    dispatch({
      type: JOURNEY_MACHINE_EVENT.navigate,
      steps,
      stepId,
      replaceHistory,
    });
    return true;
  }, [setQuestionIndex, steps]);

  const fail = useCallback((error) => {
    dispatch({ type: JOURNEY_MACHINE_EVENT.fail, error });
  }, []);

  const retry = useCallback(() => {
    dispatch({ type: JOURNEY_MACHINE_EVENT.retry });
  }, []);

  return {
    state,
    activeStep,
    goNext,
    goBack,
    goTo,
    fail,
    retry,
  };
};
